import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { markupBadgeColor } from '@/lib/grades';
import FollowHospitalButton from '@/components/FollowHospitalButton';
import { Button } from '@/components/ui/button';
import { Building2, MapPin, ChevronDown, ArrowUpDown, ExternalLink, MapPinned, Navigation, Loader2, Search, Scale } from 'lucide-react';
import { useBrand } from '@/config/brands';
import { useTheme } from '@/contexts/ThemeContext';
import { useUserLocation, type UserLocation } from '@/hooks/useUserLocation';
import { useLiveDb } from '@/lib/offlineData';
import { isGarbagePrice } from '@/lib/priceSanity';
import { loadScenarioCoverageFor, getCoverageForScenario, getHospitalInfo, getAllHospitals, haversineDistance } from '@/lib/scenarioCoverageStore';
import { CoverageProgressLoader } from '@/lib/CoverageProgressLoader';

interface NearbyHospital {
  hospital_id: string;
  hospital_name: string;
  city: string;
  state: string;
  distance_miles: number;
  matched_codes: number;
  total_codes: number;
  coverage_percentage: number;
  total_hospital_price: number;
  total_national_price: number;
  avg_markup_percentage: number;
  procedure_count: number;
  pricing_method?: string;   // 'drg' = inpatient priced from a DRG bundle
  /** Third-party quality signals from the coverage manifest (static path only; the
   *  Supabase RPC fallback has none). cs = CMS overall stars, rp = HCAHPS "would
   *  definitely recommend" %, ew = ER median minutes (OP-18b). Absent = not rated. */
  cs?: number | null;
  rp?: number | null;
  ew?: number | null;
}

type SortField = 'distance' | 'cost' | 'markup';

interface NearbyHospitalsTableProps {
  scenarioId: string | undefined;
  userLocation: UserLocation | null;
  radiusMiles: number;
  nationalTotal: number;
  selectedHospitalId: string | null;
  onHospitalSelect: (hospitalId: string, name: string) => void;
  /** Jump to a scenario section after selecting a hospital — the Comparison /
   *  Price Breakdown row buttons use this so tapping them actually SHOWS the
   *  hospital-vs-national content (modal on mobile, scroll on desktop)
   *  instead of invisibly setting state behind the hospitals modal. */
  onOpenSection?: (key: 'price-comparison' | 'code-breakdown') => void;
  /** Full-screen modal mode: show every hospital by default (no "show more"
   *  click) and let the surrounding scroll container own the height instead of
   *  capping the inner list at a fixed max-height. */
  fillHeight?: boolean;
}

/** Blended price: hospital prices for matched codes + national avg for unmatched codes */
function getBlendedPrice(h: NearbyHospital, nationalTotal: number): number {
  if (h.matched_codes >= h.total_codes || nationalTotal <= 0) return h.total_hospital_price;
  const unmatchedNational = Math.max(0, nationalTotal - h.total_national_price);
  return h.total_hospital_price + unmatchedNational;
}

function getBlendedMarkup(h: NearbyHospital, nationalTotal: number): number {
  if (nationalTotal <= 0) return h.avg_markup_percentage;
  const blended = getBlendedPrice(h, nationalTotal);
  return ((blended - nationalTotal) / nationalTotal) * 100;
}

export function NearbyHospitalsTable({
  scenarioId,
  userLocation,
  radiusMiles,
  nationalTotal,
  selectedHospitalId,
  onHospitalSelect,
  onOpenSection,
  fillHeight = false,
}: NearbyHospitalsTableProps) {
  const { t } = useTranslation(['scenarios', 'common']);
  const brand = useBrand();
  const isAhp = brand.design === 'civic';
  const { isDark } = useTheme();
  const ahpDark = isAhp && isDark;
  const navigate = useNavigate();
  const [showAll, setShowAll] = useState(fillHeight);
  const [sortBy, setSortBy] = useState<SortField>('distance');
  // Inline location controls for the no-location empty state (shared store —
  // updates propagate to the parent page's useUserLocation instance too).
  const locCtl = useUserLocation();
  const [zipInput, setZipInput] = useState('');
  const liveDb = useLiveDb();

  const { data: hospitals = [], isLoading } = useQuery({
    queryKey: ['nearby-hospitals', userLocation?.lat, userLocation?.lng, radiusMiles, scenarioId, liveDb],
    queryFn: async () => {
      if (!userLocation) return [];

      // Try static cache with client-side distance calc (unless ?db=live)
      if (!liveDb && scenarioId) {
        const loaded = await loadScenarioCoverageFor(scenarioId);
        if (loaded) {
          const rows = getCoverageForScenario(scenarioId);
          if (rows.length > 0) {
            const results: NearbyHospital[] = [];
            for (const row of rows) {
              const hospital = getHospitalInfo(row.h);
              if (!hospital || hospital.la == null || hospital.lo == null) continue;

              const dist = haversineDistance(
                userLocation.lat, userLocation.lng,
                hospital.la, hospital.lo,
              );

              if (dist > radiusMiles) continue;

              results.push({
                hospital_id: row.h,
                hospital_name: hospital.n,
                city: hospital.c,
                state: hospital.s,
                distance_miles: Math.round(dist * 10) / 10,
                matched_codes: row.mc,
                total_codes: row.tc,
                coverage_percentage: row.cp,
                total_hospital_price: row.hp,
                total_national_price: row.np,
                avg_markup_percentage: row.mp,
                procedure_count: row.tc,
                pricing_method: row.pm,
                cs: hospital.cs ?? null,
                rp: hospital.rp ?? null,
                ew: hospital.ew ?? null,
              });
            }

            results.sort((a, b) => a.distance_miles - b.distance_miles);
            return results.slice(0, 100);
          }
        }
      }

      // Supabase RPC fallback (or ?db=live)
      const { data, error } = await supabase.rpc('search_nearby_hospitals_by_location', {
        p_latitude: userLocation.lat,
        p_longitude: userLocation.lng,
        p_radius_miles: radiusMiles,
        p_scenario_id: scenarioId || null,
        p_limit: 100,
      });

      if (error) {
        console.error('Nearby hospitals error:', error);
        return [];
      }

      return (data || []) as NearbyHospital[];
    },
    enabled: !!userLocation,
  });

  // Filter + sort hospitals using blended prices.
  // Site standard for scenario pricing rows: coverage >= 50 (shards + the RPC now
  // also filter server-side; this guards stale cached data). Rows fetched without
  // a scenario are plain nearby hospitals (coverage 0) and are kept as-is.
  // isGarbagePrice drops only dimensionally-invalid unit-bug math — merely-cheap
  // rows stay visible (with "hospital-filed price" framing below).
  const sorted = useMemo(() => {
    return hospitals
      .filter((h) => {
        const coverage = h.coverage_percentage ?? 0;
        if (scenarioId && coverage < 50) return false;
        if (coverage > 0) {
          const blendedPrice = getBlendedPrice(h, nationalTotal);
          const blendedMarkup = getBlendedMarkup(h, nationalTotal);
          if (isGarbagePrice(blendedPrice, nationalTotal, blendedMarkup)) return false;
        }
        return true;
      })
      .sort((a, b) => {
        if (sortBy === 'cost') return getBlendedPrice(a, nationalTotal) - getBlendedPrice(b, nationalTotal);
        if (sortBy === 'markup') return getBlendedMarkup(a, nationalTotal) - getBlendedMarkup(b, nationalTotal);
        return a.distance_miles - b.distance_miles;
      });
  }, [hospitals, sortBy, nationalTotal, scenarioId]);

  const displayed = showAll ? sorted : sorted.slice(0, 20);

  if (!userLocation) {
    return (
      <Card
        className={isAhp ? '' : 'border-matrix-green/20 bg-black/40 backdrop-blur-sm'}
        style={isAhp ? { backgroundColor: ahpDark ? 'var(--civic-card-dark)' : '#fff', border: ahpDark ? '1px solid rgba(var(--civic-gold-rgb),0.2)' : '1px solid rgba(var(--civic-ink-rgb),0.12)', boxShadow: ahpDark ? '0 2px 16px rgba(0,0,0,0.4)' : '0 2px 16px rgba(0,0,0,0.06)' } : undefined}
      >
        <CardHeader>
          <CardTitle
            className={isAhp ? 'text-lg flex items-center gap-2' : 'text-lg font-mono text-matrix-green flex items-center gap-2'}
            style={isAhp ? { fontFamily: "'Instrument Serif', Georgia, serif", color: ahpDark ? 'var(--civic-warm)' : 'var(--civic-ink)', fontWeight: 400 } : undefined}
          >
            <MapPin className="w-5 h-5" style={isAhp ? { color: 'var(--civic-red)' } : undefined} />
            {t('scenarios:nearby.title')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {/* Inline location setter — this empty state used to say "set your
              location above", a dead end inside the mobile section modal where
              the page-level control is hidden behind it. useUserLocation is a
              shared store, so setting it here updates the parent page too. */}
          <div className="flex flex-col gap-3 max-w-sm mx-auto py-4">
            <Button
              onClick={locCtl.detectLocation}
              disabled={locCtl.isLocating}
              className={isAhp ? 'w-full' : 'w-full'}
              style={isAhp ? { backgroundColor: 'var(--civic-red)', color: '#fff' } : undefined}
            >
              {locCtl.isLocating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Navigation className="w-4 h-4 mr-2" />}
              {t('scenarios:nearby.useMyLocation', { defaultValue: 'Use my location' })}
            </Button>
            <form
              onSubmit={(e) => { e.preventDefault(); if (/^\d{5}$/.test(zipInput)) locCtl.lookupZip(zipInput); }}
              className="flex gap-2"
            >
              <input
                inputMode="numeric"
                maxLength={5}
                value={zipInput}
                onChange={(e) => setZipInput(e.target.value.replace(/\D/g, ''))}
                placeholder={t('scenarios:nearby.zipPlaceholder', { defaultValue: 'Or enter ZIP code' })}
                className="flex-1 px-3 py-2 rounded-md text-base outline-none"
                style={{
                  border: ahpDark ? '1px solid rgba(var(--civic-gold-rgb),0.3)' : '1px solid rgba(var(--civic-ink-rgb),0.15)',
                  background: ahpDark ? 'rgba(0,0,0,0.2)' : '#fff',
                  color: ahpDark ? 'var(--civic-warm)' : 'var(--civic-ink)',
                }}
              />
              <Button type="submit" variant="outline" disabled={!/^\d{5}$/.test(zipInput) || locCtl.isLocating}>
                <Search className="w-4 h-4" />
              </Button>
            </form>
            {locCtl.error && <p className="text-xs text-center" style={{ color: 'var(--civic-red)' }}>{locCtl.error}</p>}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card
      className={isAhp ? '' : 'border-matrix-green/20 bg-black/40 backdrop-blur-sm'}
      style={isAhp ? { backgroundColor: ahpDark ? 'var(--civic-card-dark)' : '#fff', border: ahpDark ? '1px solid rgba(var(--civic-gold-rgb),0.2)' : '1px solid rgba(var(--civic-ink-rgb),0.12)', boxShadow: ahpDark ? '0 2px 16px rgba(0,0,0,0.4)' : '0 2px 16px rgba(0,0,0,0.06)' } : undefined}
    >
      <CardHeader className="pb-2">
        <CardTitle
          className={isAhp ? 'text-lg flex items-center gap-2' : 'text-lg font-mono text-matrix-green flex items-center gap-2'}
          style={isAhp ? { fontFamily: "'Instrument Serif', Georgia, serif", color: ahpDark ? 'var(--civic-warm)' : 'var(--civic-ink)', fontWeight: 400 } : undefined}
        >
          <MapPin className="w-5 h-5" style={isAhp ? { color: 'var(--civic-red)' } : undefined} />
          {t('scenarios:nearby.title')}
        </CardTitle>
        <CardDescription className="text-xs">
          {isLoading ? t('scenarios:nearby.searching') : t('scenarios:nearby.withinSummary', { count: sorted.length, radius: radiusMiles, label: userLocation.label })}
        </CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <CoverageProgressLoader />
        ) : sorted.length === 0 ? (
          <div className="text-center py-6 text-sm text-muted-foreground">
            {t('scenarios:nearby.noneFound', { radius: radiusMiles })}
          </div>
        ) : (
          <>
            {/* Sort controls */}
            <div className="flex gap-1.5 mb-3">
              {(['distance', 'cost', 'markup'] as SortField[]).map((field) => (
                <button
                  key={field}
                  onClick={() => setSortBy(field)}
                  className={`text-xs px-2.5 py-1 rounded-full transition-colors ${
                    sortBy === field
                      ? isAhp
                        ? 'bg-civic-navy text-white'
                        : 'bg-matrix-green text-black'
                      : isAhp
                        ? (ahpDark ? 'bg-white/10 text-gray-300 hover:bg-white/15' : 'bg-gray-100 text-gray-600 hover:bg-gray-200')
                        : 'bg-white/5 text-gray-400 hover:bg-white/10'
                  }`}
                >
                  {field === 'distance' ? t('scenarios:nearby.sortNearest') : field === 'cost' ? t('scenarios:nearby.sortLowestCost') : t('scenarios:nearby.sortBestValue')}
                </button>
              ))}
            </div>

            {/* Hospital list */}
            <div className={fillHeight ? "space-y-1.5" : "space-y-1.5 max-h-[420px] overflow-y-auto thin-scrollbar"}>
              {displayed.map((h) => {
                const isSelected = h.hospital_id === selectedHospitalId;
                const hasPricing = h.coverage_percentage > 0;
                const blendedPrice = getBlendedPrice(h, nationalTotal);
                const blendedMarkup = getBlendedMarkup(h, nationalTotal);
                const isPartial = h.matched_codes < h.total_codes;
                const abs = Math.abs(blendedMarkup);
                // Near-zero price framing (DISPLAY only — never hide filed prices):
                // under $100 or under 1% of national is almost certainly a filing
                // artifact. Amber "hospital-filed price" label instead of a green
                // "% cheaper" celebration.
                const suspiciouslyCheap = hasPricing
                  && blendedPrice < Math.max(100, 0.01 * nationalTotal);

                // Varying shades: deeper color = bigger magnitude
                const { borderColor, priceColor, bgTint, badgeBg, badgeText } = !hasPricing
                  ? { borderColor: ahpDark ? 'rgba(255,255,255,0.08)' : '#e5e7eb', priceColor: undefined, bgTint: 'transparent', badgeBg: 'transparent', badgeText: '' }
                  : suspiciouslyCheap
                    ? { borderColor: '#d97706', priceColor: '#b45309', bgTint: 'rgba(217,119,6,0.05)', badgeBg: 'rgba(217,119,6,0.10)', badgeText: '#b45309' }  // amber — unverified filed price
                  : blendedMarkup <= -50
                    ? { borderColor: '#15803d', priceColor: '#15803d', bgTint: 'rgba(22,163,74,0.06)', badgeBg: 'rgba(22,163,74,0.12)', badgeText: '#15803d' }  // deep green
                  : blendedMarkup <= -20
                    ? { borderColor: '#22c55e', priceColor: '#16a34a', bgTint: 'rgba(34,197,94,0.04)', badgeBg: 'rgba(34,197,94,0.10)', badgeText: '#16a34a' }  // medium green
                  : blendedMarkup <= 0
                    ? { borderColor: '#86efac', priceColor: '#22c55e', bgTint: 'rgba(134,239,172,0.04)', badgeBg: 'rgba(34,197,94,0.08)', badgeText: '#22c55e' }  // light green
                  : blendedMarkup <= 20
                    ? { borderColor: '#fca5a5', priceColor: '#ef4444', bgTint: 'rgba(252,165,165,0.04)', badgeBg: 'rgba(239,68,68,0.08)', badgeText: '#ef4444' }  // light red
                  : blendedMarkup <= 50
                    ? { borderColor: '#ef4444', priceColor: '#dc2626', bgTint: 'rgba(239,68,68,0.04)', badgeBg: 'rgba(220,38,38,0.10)', badgeText: '#dc2626' }  // medium red
                    : { borderColor: '#991b1b', priceColor: '#991b1b', bgTint: 'rgba(153,27,27,0.06)', badgeBg: 'rgba(153,27,27,0.12)', badgeText: '#991b1b' }; // deep red

                return (
                  <div
                    key={h.hospital_id}
                    className={`rounded-lg border-l-[3px] transition-colors ${
                      isSelected
                        ? isAhp ? 'ring-1 ring-civic-navy/30' : 'ring-1 ring-matrix-green/30'
                        : ''
                    }`}
                    style={{ borderLeftColor: borderColor, backgroundColor: bgTint }}
                  >
                    {/* Main clickable area — selects hospital for breakdown */}
                    <button
                      onClick={() => onHospitalSelect(h.hospital_id, h.hospital_name)}
                      className={`w-full text-left p-2.5 rounded-r-lg transition-colors ${
                        isAhp
                          ? (ahpDark ? 'hover:bg-white/5' : 'hover:bg-gray-50/80')
                          : 'hover:bg-matrix-green/5'
                      }`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <div
                            className={`text-sm font-medium truncate ${isAhp ? '' : 'text-white'}`}
                            style={isAhp ? { color: ahpDark ? 'var(--civic-warm)' : 'var(--civic-ink)' } : undefined}
                          >
                            {h.hospital_name}
                          </div>
                          <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                            <span className="text-xs text-muted-foreground">
                              {h.city}, {h.state}
                            </span>
                            <span className={`text-[11px] font-medium px-1.5 rounded-full ${isAhp ? (ahpDark ? 'bg-white/10' : 'bg-gray-100') : 'bg-white/10'}`}
                              style={{ color: isAhp ? (ahpDark ? 'var(--civic-gold)' : 'var(--civic-navy)') : '#00ff00' }}
                            >
                              {h.distance_miles.toFixed(1)} mi
                            </span>
                            {hasPricing && (
                              <span className="text-[10px] text-muted-foreground">
                                {t('common:pricing.codesCount', { matched: h.matched_codes, total: h.total_codes })}
                              </span>
                            )}
                            {h.pricing_method === 'drg' && (
                              <span className="text-[10px] font-semibold px-1.5 rounded-full"
                                style={{ backgroundColor: ahpDark ? 'rgba(var(--civic-navy-soft-rgb),0.18)' : 'rgba(var(--civic-navy-rgb),0.08)', color: ahpDark ? 'var(--civic-navy-soft)' : 'var(--civic-navy)' }}
                                title={t('scenarios:nearby.drgBundleTooltip')}>
                                {t('scenarios:nearby.drgBundle')}
                              </span>
                            )}
                          </div>
                          {/* Quality row (Trent, 2026-09-05): CMS stars, HCAHPS recommend %, ER wait.
                              All three are federal CMS figures about the hospital — the recommend
                              share is what ITS OWN past patients said on the HCAHPS survey, never an
                              AHP recommendation; the tooltips say so. */}
                          {(h.cs != null || h.rp != null || h.ew != null) && (
                            <div className="flex items-center gap-x-2.5 gap-y-0.5 mt-1 flex-wrap text-[11px] font-mono tabular-nums"
                              style={{ color: ahpDark ? 'rgba(var(--civic-warm-rgb),0.75)' : isAhp ? 'rgba(var(--civic-ink-rgb),0.7)' : '#9ca3af' }}>
                              {h.cs != null && (
                                <span title={t('scenarios:nearby.cmsStarsTooltip', { defaultValue: "CMS Care Compare overall star rating (1–5). CMS's rating, not ours." })}>
                                  <span style={{ color: ahpDark ? 'var(--civic-gold)' : 'var(--civic-red)' }}>{'★'.repeat(h.cs)}</span><span className="opacity-40">{'★'.repeat(Math.max(0, 5 - h.cs))}</span>
                                  <span className="ml-1 font-sans opacity-70">{t('scenarios:nearby.cmsStars', { defaultValue: 'CMS' })}</span>
                                </span>
                              )}
                              {h.rp != null && (
                                <span title={t('scenarios:nearby.recommendTooltip', { defaultValue: 'HCAHPS, the federal patient survey run by CMS. Discharged patients are asked whether they would recommend this hospital to friends and family; this is the share of ITS OWN past patients who answered "definitely yes". A CMS survey result, not an American Hospital Prices recommendation.' })}>
                                  {h.rp}% <span className="font-sans opacity-70">{t('scenarios:nearby.wouldRecommend', { defaultValue: 'would recommend' })}</span>
                                </span>
                              )}
                              {h.ew != null && (
                                <span title={t('scenarios:nearby.erWaitTooltip', { defaultValue: 'Median minutes a patient spends in this hospital\'s emergency department from arrival to leaving (CMS timely & effective care measure OP-18b).' })}>
                                  <span className="font-sans opacity-70">{t('scenarios:nearby.erWait', { defaultValue: 'ER' })}</span> {h.ew} <span className="font-sans opacity-70">{t('scenarios:nearby.minutes', { defaultValue: 'min' })}</span>
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                        <div className="text-right shrink-0">
                          {hasPricing ? (
                            <>
                              <div className="text-sm font-bold font-mono" style={{ color: priceColor }}>
                                {isPartial ? '~' : ''}${blendedPrice.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                              </div>
                              <span
                                className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full inline-block mt-0.5"
                                style={{ backgroundColor: badgeBg, color: badgeText }}
                                title={suspiciouslyCheap
                                  ? t('common:pricing.hospitalFiledPriceTooltip')
                                  : undefined}
                              >
                                {suspiciouslyCheap
                                  ? t('common:pricing.hospitalFiledPrice')
                                  : blendedMarkup <= 0
                                  ? (abs >= 99.5 ? t('common:pricing.belowAvgExtreme') : t('common:pricing.percentCheaper', { percent: abs.toFixed(0) }))
                                  : t('common:pricing.percentAboveAvg', { percent: abs.toFixed(0) })
                                }
                              </span>
                            </>
                          ) : (
                            <span className="text-xs text-muted-foreground">{t('common:pricing.noPricing')}</span>
                          )}
                        </div>
                      </div>
                    </button>
                    {/* Action row — links to profile, map, breakdown */}
                    <div className={`flex items-center gap-0.5 px-2.5 pb-1.5 -mt-0.5`}>
                      <a
                        href={`/hospital/${h.hospital_id}`}
                        onClick={(e) => { e.preventDefault(); navigate(`/hospital/${h.hospital_id}`); }}
                        title={t('scenarios:nearby.profileTitle')}
                        className={`flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded transition-colors ${
                          isAhp
                            ? (ahpDark ? 'text-muted-foreground hover:text-civic-warm hover:bg-white/10' : 'text-muted-foreground hover:text-gray-700 hover:bg-gray-100')
                            : 'text-gray-500 hover:text-matrix-green hover:bg-white/10'
                        }`}
                      >
                        <Building2 className="w-3 h-3" />
                        {t('scenarios:nearby.profile')}
                      </a>
                      <a
                        href={`/map/${h.state.toLowerCase()}/${h.hospital_id}`}
                        onClick={(e) => { e.preventDefault(); navigate(`/map/${h.state.toLowerCase()}/${h.hospital_id}`); }}
                        title={t('scenarios:nearby.mapTitle')}
                        className={`flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded transition-colors ${
                          isAhp
                            ? (ahpDark ? 'text-muted-foreground hover:text-civic-warm hover:bg-white/10' : 'text-muted-foreground hover:text-gray-700 hover:bg-gray-100')
                            : 'text-gray-500 hover:text-matrix-green hover:bg-white/10'
                        }`}
                      >
                        <MapPinned className="w-3 h-3" />
                        {t('scenarios:nearby.map')}
                      </a>
                      {/* Was a single "Breakdown" button that only set state —
                          from inside the mobile hospitals modal nothing visibly
                          happened. Split into the two destinations it implied. */}
                      <button
                        onClick={() => { onHospitalSelect(h.hospital_id, h.hospital_name); onOpenSection?.('code-breakdown'); }}
                        title={t('scenarios:nearby.priceBreakdownTitle', { defaultValue: 'Per-code prices at this hospital' })}
                        className={`flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded transition-colors ${
                          isAhp
                            ? (ahpDark ? 'text-muted-foreground hover:text-civic-warm hover:bg-white/10' : 'text-muted-foreground hover:text-gray-700 hover:bg-gray-100')
                            : 'text-gray-500 hover:text-matrix-green hover:bg-white/10'
                        }`}
                      >
                        <ArrowUpDown className="w-3 h-3" />
                        {t('scenarios:nearby.priceBreakdown', { defaultValue: 'Price Breakdown' })}
                      </button>
                      <button
                        onClick={() => { onHospitalSelect(h.hospital_id, h.hospital_name); onOpenSection?.('price-comparison'); }}
                        title={t('scenarios:nearby.comparisonTitle', { defaultValue: 'Compare this hospital vs the national average' })}
                        className={`flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded transition-colors ${
                          isAhp
                            ? (ahpDark ? 'text-muted-foreground hover:text-civic-warm hover:bg-white/10' : 'text-muted-foreground hover:text-gray-700 hover:bg-gray-100')
                            : 'text-gray-500 hover:text-matrix-green hover:bg-white/10'
                        }`}
                      >
                        <Scale className="w-3 h-3" />
                        {t('scenarios:nearby.comparison', { defaultValue: 'Comparison' })}
                      </button>
                      <FollowHospitalButton
                        hospital={{ id: h.hospital_id, name: h.hospital_name, city: h.city, state: h.state }}
                        dark={ahpDark}
                        withLabel
                        size={13}
                        className="ml-auto text-[10px] px-1.5 py-0.5 rounded hover:bg-white/10"
                      />
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Show more */}
            {sorted.length > 20 && !showAll && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowAll(true)}
                className="w-full mt-2 text-xs"
              >
                <ChevronDown className="w-3.5 h-3.5 mr-1" />
                {t('scenarios:nearby.showMore', { count: sorted.length - 20 })}
              </Button>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

function MarkupBadge({ markup, isAhp }: { markup: number; isAhp: boolean }) {
  const isNegative = markup < 0;
  const color = markupBadgeColor(markup);

  return (
    <span
      className="text-[10px] font-medium px-1.5 py-0.5 rounded-full inline-block mt-0.5"
      style={{ backgroundColor: `${color}20`, color }}
    >
      {isNegative ? '' : '+'}{markup.toFixed(0)}%
    </span>
  );
}
